import { useState, useMemo, useEffect, useCallback } from 'react';
import { useServerInfiniteScroll } from '../hooks/useServerInfiniteScroll';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Plus, Edit, Calendar, Trash2, Search, AlertTriangle } from 'lucide-react';
import { Program, Track } from '../data/mockData';
import { useListTracksQuery, useListProgramsPaginatedQuery } from '../../store/api';
import { CreateProgramDrawer } from './CreateProgramDrawer';
import { UpdateProgramDrawer } from './UpdateProgramDrawer';
import { ScheduleProgramDrawer } from './ScheduleProgramDrawerDuration';
import { format } from 'date-fns';
import { parseApiDate } from '../utils/dateTime';

interface ProgramsTabProps {
  channelId: number | string;
  onSaveProgram: (program: Omit<Program, 'id' | 'channelId'>) => Promise<void>;
  onUpdateProgram: (
    programId: string | number,
    program: Omit<Program, 'id' | 'channelId'>,
    editMode?: 'single' | 'series'
  ) => Promise<void>;
  onDeleteProgram: (programId: string | number) => Promise<void>;
  onUploadTrack: (file: File) => Promise<void>;
}

export function ProgramsTab({
  channelId,
  onSaveProgram,
  onUpdateProgram,
  onDeleteProgram,
  onUploadTrack,
}: ProgramsTabProps) {
  const { data: tracksData = [] } = useListTracksQuery(Number(channelId));
  const tracks: Track[] = (tracksData as any[]).map((t: any) => ({
    ...t,
    id: String(t.id),
    channelId: String(t.channelId ?? t.channel_id),
    fileName: t.fileName ?? t.file_name,
    uploadDate: t.uploadDate ?? t.upload_date,
  }));
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isUpdateDrawerOpen, setIsUpdateDrawerOpen] = useState(false);
  const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [programToSchedule, setprogramToSchedule] = useState<Omit<Program, 'id' | 'channelId'> | null>(null);
  const [selectedProgramIds, setSelectedProgramIds] = useState<Set<string | number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'single' | 'bulk'; id?: string | number } | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Server-side infinite scroll
  const [pageOffset, setPageOffset] = useState(0);
  const PAGE_LIMIT = 20;

  const { data: programsData, isFetching } = useListProgramsPaginatedQuery({
    channelId: Number(channelId),
    offset: pageOffset,
    limit: PAGE_LIMIT,
    search: debouncedSearch || undefined,
  });

  const loadMore = useCallback(() => {
    if (programsData?.hasMore && !isFetching) {
      setPageOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [programsData?.hasMore, isFetching]);

  const { sentinelRef } = useServerInfiniteScroll({
    hasMore: programsData?.hasMore ?? false,
    isFetching,
    onLoadMore: loadMore,
  });

  // Reset pagination when search changes
  useEffect(() => {
    setPageOffset(0);
  }, [debouncedSearch]);

  const programs: Program[] = useMemo(() =>
    (programsData?.items ?? []).map((p: any) => ({
      ...p,
      id: String(p.id),
      channelId: String(p.channelId ?? p.channel_id),
      trackId: p.trackId ?? p.track_id ?? null,
      startDateTime: p.startDateTime ?? p.start_date_time,
      endDateTime: p.endDateTime ?? p.end_date_time,
      scheduleType: p.scheduleType ?? p.schedule_type,
    })),
    [programsData?.items]
  );

  const getHighlightClass = (program: Program) => {
    const hasTrack = program.trackId && String(program.trackId) !== '__none__';
    if (hasTrack) return '';

    const now = new Date();
    const startTime = parseApiDate(program.startDateTime);
    const hoursUntilStart = (startTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursUntilStart <= 24 && hoursUntilStart > 0) {
      return 'border-l-4 border-l-red-500 bg-red-950/30';
    }
    return 'border-l-4 border-l-yellow-500 bg-yellow-950/20';
  };

  const handleEdit = (program: Program) => {
    setSelectedProgram(program);
    setIsUpdateDrawerOpen(true);
  };

  const handleSchedule = (program: Program) => {
    setSelectedProgram(program);
    setprogramToSchedule({
      title: program.title,
      trackId: program.trackId,
      startDateTime: program.startDateTime,
      endDateTime: program.endDateTime,
      scheduleType: program.scheduleType,
      recurrence: program.recurrence,
      description: program.description,
      thumbnail: program.thumbnail,
    });
    setIsScheduleDrawerOpen(true);
  };

  const handleCreateWithSchedule = (programData: Omit<Program, 'id' | 'channelId'>) => {
    setprogramToSchedule(programData);
    setIsCreateDrawerOpen(false);
    setIsScheduleDrawerOpen(true);
  };

  const handleSaveSchedule = async (scheduleData: Omit<Program, 'id' | 'channelId'>) => {
    if (selectedProgram) {
      await onUpdateProgram(selectedProgram.id, scheduleData);
    } else if (programToSchedule) {
      await onSaveProgram(scheduleData);
    }
    setIsScheduleDrawerOpen(false);
    setSelectedProgram(null);
    setprogramToSchedule(null);
  };

  const handleBackToProgram = () => {
    setIsScheduleDrawerOpen(false);
    if (selectedProgram) {
      setIsUpdateDrawerOpen(true);
      return;
    }
    setIsCreateDrawerOpen(true);
  };

  const toggleProgramSelection = (programId: string | number) => {
    setSelectedProgramIds((prev) => {
      const next = new Set(prev);
      if (next.has(programId)) {
        next.delete(programId);
      } else {
        next.add(programId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProgramIds.size === programs.length) {
      setSelectedProgramIds(new Set());
      return;
    }
    setSelectedProgramIds(new Set(programs.map((p) => p.id)));
  };

  const handleDeleteSelected = () => {
    if (selectedProgramIds.size === 0) return;
    setDeleteConfirm({ type: 'bulk' });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirm) return;

    if (deleteConfirm.type === 'single' && deleteConfirm.id !== undefined) {
      onDeleteProgram(deleteConfirm.id);
    } else if (deleteConfirm.type === 'bulk') {
      selectedProgramIds.forEach((id) => onDeleteProgram(id));
      setSelectedProgramIds(new Set());
    }
    setDeleteConfirm(null);
  };

  const getTrackName = (trackId: string | number | null) => {
    if (!trackId) return null;
    const track = tracks.find((t) => String(t.id) === String(trackId));
    return track?.fileName || null;
  };

  const formatNextRun = (program: Program) => {
    try {
      return format(parseApiDate(program.startDateTime), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Programs</h2>
          <p className="text-gray-400 mt-1">Create and manage program schedules</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleDeleteSelected}
            disabled={selectedProgramIds.size === 0}
            className="border-gray-700 text-black hover:text-white hover:bg-gray-800"
          >
            Delete Selected
          </Button>
          <Button
            onClick={() => setIsCreateDrawerOpen(true)}
            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="size-4" />
            Create New Program
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-500" />
        <Input
          placeholder="Search by title or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-10"
        />
      </div>

      {/* Programs Table */}
      {programs.length === 0 && !isFetching ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <Calendar className="size-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400 text-lg">
            {!debouncedSearch ? 'No programs created yet' : 'No programs match your search'}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {!debouncedSearch ? 'Click "Create New Program" to get started' : 'Try a different search term'}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-800 bg-gray-900 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400 w-12">
                  <input
                    type="checkbox"
                    checked={programs.length > 0 && selectedProgramIds.size === programs.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all programs"
                  />
                </TableHead>
                <TableHead className="text-gray-400">Program Title</TableHead>
                <TableHead className="text-gray-400 hidden sm:table-cell">Track File</TableHead>
                <TableHead className="text-gray-400 hidden md:table-cell">Start Time</TableHead>
                <TableHead className="text-gray-400 hidden sm:table-cell">Schedule Type</TableHead>
                <TableHead className="text-gray-400 hidden lg:table-cell">Frequency</TableHead>
                <TableHead className="text-gray-400 hidden lg:table-cell">Repeat On</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {programs.map((program) => {
                const trackName = getTrackName(program.trackId);
                const highlightClass = getHighlightClass(program);

                return (
                  <TableRow
                    key={program.id}
                    className={`border-gray-800 hover:bg-gray-800/60 ${highlightClass}`}
                  >
                    <TableCell className="text-gray-400">
                      <input
                        type="checkbox"
                        checked={selectedProgramIds.has(program.id)}
                        onChange={() => toggleProgramSelection(program.id)}
                        aria-label={`Select ${program.title}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{program.title}</span>
                        {!trackName && (
                          <AlertTriangle className="size-3.5 text-yellow-400 shrink-0" />
                        )}
                      </div>
                      {program.description && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{program.description}</p>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {trackName ? (
                        <span className="text-gray-400">{trackName}</span>
                      ) : (
                        <Badge variant="outline" className="text-yellow-400 border-yellow-600/50 text-xs">
                          No Track
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-400 hidden md:table-cell">
                      {formatNextRun(program)}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge
                        variant="outline"
                        className={program.scheduleType === 'recurring'
                          ? 'text-purple-400 border-purple-600/50 text-xs'
                          : 'text-gray-400 border-gray-600/50 text-xs'}
                      >
                        {program.scheduleType === 'recurring' ? 'Recurring' : 'One-time'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-400 text-xs hidden lg:table-cell">
                      {program.scheduleType === 'recurring' && program.recurrence
                        ? program.recurrence.frequency === 'daily' ? 'Daily' : 'Weekly'
                        : '—'}
                    </TableCell>
                    <TableCell className="text-gray-400 text-xs hidden lg:table-cell">
                      {program.scheduleType === 'recurring' && program.recurrence?.frequency === 'weekly' && program.recurrence.daysOfWeek
                        ? program.recurrence.daysOfWeek
                            .slice().sort((a: number, b: number) => a - b)
                            .map((d: number) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d])
                            .join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(program)}
                          className="text-gray-400 hover:text-white hover:bg-gray-800"
                        >
                          <Edit className="size-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSchedule(program)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Calendar className="size-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteConfirm({ type: 'single', id: program.id })}
                          className="text-gray-400 hover:text-red-400 hover:bg-gray-800"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {(programsData?.hasMore ?? false) && (
        <div ref={sentinelRef} className="flex justify-center py-6">
          <div className="text-sm text-gray-500">Loading more...</div>
        </div>
      )}
      {isFetching && programs.length === 0 && (
        <div className="flex justify-center py-6">
          <div className="text-sm text-gray-500">Loading programs...</div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {deleteConfirm?.type === 'bulk'
                ? `Are you sure you want to delete ${selectedProgramIds.size} selected program(s)? This action uses a soft delete.`
                : 'Are you sure you want to delete this program? This action uses a soft delete.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Drawers */}
      <CreateProgramDrawer
        isOpen={isCreateDrawerOpen}
        onClose={() => {
          setIsCreateDrawerOpen(false);
          setprogramToSchedule(null);
        }}
        onContinue={handleCreateWithSchedule}
        tracks={tracks}
        channelId={channelId}
        onUploadTrack={onUploadTrack}
        initialData={programToSchedule}
      />

      {selectedProgram && (
        <UpdateProgramDrawer
          isOpen={isUpdateDrawerOpen}
          onClose={() => {
            setIsUpdateDrawerOpen(false);
            setSelectedProgram(null);
          }}
          onUpdate={async (data, editMode) => {
            await onUpdateProgram(selectedProgram.id, data, editMode);
            setIsUpdateDrawerOpen(false);
            setSelectedProgram(null);
          }}
          onGoToSchedule={(data) => {
            setprogramToSchedule(data);
            setIsUpdateDrawerOpen(false);
            setIsScheduleDrawerOpen(true);
          }}
          program={selectedProgram}
          tracks={tracks}
          channelId={channelId}
        />
      )}

      <ScheduleProgramDrawer
        isOpen={isScheduleDrawerOpen}
        onClose={() => {
          setIsScheduleDrawerOpen(false);
          setSelectedProgram(null);
          setprogramToSchedule(null);
        }}
        onBack={handleBackToProgram}
        onSave={handleSaveSchedule}
        existingProgram={programToSchedule}
        tracks={tracks}
        programs={programs}
      />
    </div>
  );
}